/**
 * FREE GIFT
 * ---------------------------------------------------------------------------
 * Keeps one — and only one — free gift line in the cart:
 *
 *   - as soon as at least one qualifying ("pillow type") product is in the cart,
 *     the gift is added with a quantity of 1;
 *   - the quantity stays 1 no matter how many pillows, or how many units of each
 *     pillow, the customer has;
 *   - the moment no qualifying product is left, the gift is removed.
 *
 * The gift line is rendered without a remove button, without a quantity selector
 * and without links (see mini-cart.liquid / main-cart.liquid), so this file is
 * the only thing that ever touches it.
 *
 * Configured through Theme settings > Free gift, exposed by the
 * "free-gift-variables" snippet as window.freeGiftSettings.
 */

(function () {
  'use strict';

  var config = window.freeGiftSettings;

  if (!config || !config.enabled || !config.variantId) {
    return;
  }

  var routes = (window.themeVariables && window.themeVariables.routes) || {};
  var rootUrl = routes.rootUrlWithoutSlash !== undefined ? routes.rootUrlWithoutSlash : '';
  var cartUrl = routes.cartUrl || rootUrl + '/cart';
  var cartAddUrl = routes.cartAddUrl || cartUrl + '/add';
  var cartChangeUrl = routes.cartChangeUrl || cartUrl + '/change';

  var isCartPage = !!(window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart');

  // The mini-cart section is re-rendered by the theme, so we ask Shopify to send
  // the fresh markup back with every cart mutation we perform.
  var CART_SECTION = 'mini-cart';

  // Must land after the theme's own re-render, which can be delayed by up to
  // 600ms after "variant:added", otherwise our fresher markup gets overwritten.
  var REPLACEMENT_DELAY = 800;

  // The theme fires "cart:updated" and "cart:refresh" back to back for a single
  // customer action, so events are coalesced before a check runs.
  var COALESCE_DELAY = 60;

  var busy = false;
  var queued = false;
  var coalesceTimer = null;

  /* ------------------------------------------------------------------ */
  /* Matching rules                                                      */
  /* ------------------------------------------------------------------ */

  var triggerProductIds = (config.triggerProductIds || []).map(Number);
  var triggerTypes = (config.triggerTypes || []).map(function (value) {
    return String(value).toLowerCase();
  });
  var triggerKeywords = (config.triggerKeywords || []).map(function (value) {
    return String(value).toLowerCase();
  });

  function isGiftLine(item) {
    return Number(item.product_id) === Number(config.productId);
  }

  /**
   * A line qualifies when it is not the gift itself and it matches any of the
   * three configurable rules: explicit product, product type, or keyword.
   */
  function isQualifyingLine(item) {
    if (isGiftLine(item)) {
      return false;
    }

    if (triggerProductIds.indexOf(Number(item.product_id)) !== -1) {
      return true;
    }

    var type = String(item.product_type || '').toLowerCase();

    if (type && triggerTypes.indexOf(type) !== -1) {
      return true;
    }

    if (triggerKeywords.length) {
      var haystack = [
        item.product_title || item.title || '',
        item.handle || ''
      ].join(' ').toLowerCase();

      for (var i = 0; i < triggerKeywords.length; i++) {
        if (haystack.indexOf(triggerKeywords[i]) !== -1) {
          return true;
        }
      }
    }

    return false;
  }

    /**
   * A line disqualifies the cart from the gift when it is neither the gift
   * itself nor a qualifying product. As soon as one of these is present —
   * whatever order it was added in — the gift is not earned.
   */
  function isDisqualifyingLine(item) {
    return !isGiftLine(item) && !isQualifyingLine(item);
  }



  /* ------------------------------------------------------------------ */
  /* Cart helpers                                                        */
  /* ------------------------------------------------------------------ */

  function fetchCart() {
    return fetch(cartUrl + '.js', {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (response) {
      return response.json();
    });
  }

  function needsSections() {
    return !isCartPage && document.querySelector('cart-drawer') !== null;
  }

  function addGift() {
    var body = {
      items: [
        {
          id: Number(config.variantId),
          quantity: 1,
          properties: { _free_gift: 'true' }
        }
      ]
    };

    if (needsSections()) {
      body.sections = [CART_SECTION];
    }

    return fetch(cartAddUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().catch(function () {
        return null;
      }).then(function (json) {
        // Gift sold out / unpublished: fail silently and, crucially, report it as
        // "not changed" so the cart page does not reload in a loop.
        return { ok: response.ok, sections: json && json.sections };
      });
    });
  }

  function changeGiftQuantity(lineKey, quantity) {
    var body = { id: lineKey, quantity: quantity };

    if (needsSections()) {
      body.sections = [CART_SECTION];
    }

    return fetch(cartChangeUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().catch(function () {
        return null;
      }).then(function (json) {
        return { ok: response.ok, sections: json && json.sections };
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Reconciliation                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * Brings the cart in line with the rules. Resolves with the list of mutations
   * that were performed (empty when the cart was already correct), plus the
   * section markup returned by the last mutation.
   */
  function reconcile(cart) {
    var items = (cart && cart.items) || [];
    var giftLines = items.filter(isGiftLine);
    var hasQualifying = items.some(isQualifyingLine);

    var hasDisqualifying = items.some(isDisqualifyingLine);

    // The gift is only earned when every non-gift line in the cart is a
    // qualifying ("pillow type") product. A single non-qualifying item is
    // enough to withhold or remove the gift, regardless of whether it was
    // added to the cart before or after the qualifying product.
    var shouldHaveGift = hasQualifying && !hasDisqualifying;

    var operations = [];

    if (shouldHaveGift) {
      if (giftLines.length === 0) {
        // Nothing to do if the gift itself cannot be bought right now.
        if (config.available === false) {
          return Promise.resolve({ changed: false, sections: null });
        }
        operations.push(function () {
          return addGift();
        });
      } else {
        // Keep the first gift line at exactly one unit, drop any duplicate line
        // (can happen if the gift was added manually or with other properties).
        giftLines.forEach(function (line, index) {
          if (index === 0) {
            if (line.quantity !== 1) {
              operations.push(function () {
                return changeGiftQuantity(line.key, 1);
              });
            }
          } else {
            operations.push(function () {
              return changeGiftQuantity(line.key, 0);
            });
          }
        });
      }
    } else if (giftLines.length) {
      // No pillow left in the cart — the gift goes away with it.
      giftLines.forEach(function (line) {
        operations.push(function () {
          return changeGiftQuantity(line.key, 0);
        });
      });
    }

    if (!operations.length) {
      return Promise.resolve({ changed: false, sections: null });
    }

    var outcome = { changed: false, sections: null };

    return operations.reduce(function (chain, operation) {
      return chain.then(function () {
        return operation().then(function (result) {
          if (result && result.ok) {
            outcome.changed = true;
            outcome.sections = result.sections || outcome.sections;
          }
        });
      });
    }, Promise.resolve()).then(function () {
      return outcome;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Broadcasting the result back to the theme                           */
  /* ------------------------------------------------------------------ */

  function broadcast(sections) {
    return fetchCart().then(function (cart) {
      if (sections) {
        cart.sections = sections;
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cart, freeGift: true }
      }));

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: cart,
          freeGift: true,
          replacementDelay: REPLACEMENT_DELAY
        }
      }));

      return cart;
    });
  }

  /**
   * Safety net for the cart page: should the cart ever refuse to settle (an app
   * or a Shopify Function fighting us over the gift line), stop reloading rather
   * than trapping the customer in a reload loop.
   */
  function allowReload() {
    var MAX_RELOADS = 3;

    try {
      var count = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10) + 1;

      if (count > MAX_RELOADS) {
        return false;
      }

      sessionStorage.setItem(RELOAD_KEY, String(count));
    } catch (e) {
      // Private browsing — fall through and reload, the cart page is not a loop
      // risk in practice, this is only a guard.
    }

    return true;
  }

  function clearReloadGuard() {
    try {
      sessionStorage.removeItem(RELOAD_KEY);
    } catch (e) {
      // Nothing to do.
    }
  }

  function run(cart) {
    if (busy) {
        // Something changed while we were working — check again once we are done.
        queued = true;
        return Promise.resolve();
    }

    busy = true;

    return Promise.resolve(cart || fetchCart())
      .then(reconcile)
      .then(function (result) {
        if (!result.changed) {
          return null;
        }

        if (isCartPage) {
          // The cart page is rendered server side; a reload is the only way to
          // get consistent totals, discounts and line numbering.
          if (allowReload()) {
            window.location.reload();
          }
          return null;
        }

        return broadcast(result.sections);
      })
      .catch(function (error) {
        console.error('[free-gift]', error);
      })
      .then(function () {
        busy = false;

        if (queued) {
          queued = false;
          run(null);
        }
      });
  }

  function schedule(cart) {
    clearTimeout(coalesceTimer);
    coalesceTimer = setTimeout(function () {
      run(cart);
    }, COALESCE_DELAY);
  }

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  function onCartEvent(event) {
    // Ignore the events we dispatched ourselves, otherwise we loop forever.
    if (event && event.detail && event.detail.freeGift) {
      return;
    }

    var cart = event && event.detail && event.detail.cart;

    // The detail payload of "cart:refresh" is not always a full cart object, so
    // only trust it when it actually carries the line items.
    schedule(cart && Array.isArray(cart.items) ? cart : null);
  }

  document.documentElement.addEventListener('cart:updated', onCartEvent);
  document.documentElement.addEventListener('cart:refresh', onCartEvent);

  // First pass, using the snapshot rendered by Liquid so no extra request is
  // made when the cart is already correct (which is the common case).
  var initialCart = config.initialCart;

  if (initialCart && initialCart.item_count > 0) {
    run(initialCart);
  }
})();
